import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { findAssistantRefElements } from '@/utils/assistantRefs';

interface RefLike {
  n: number;
  targetId: string;
}

const PULSE_MS = 600;

const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' &&
  !!window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export function useAssistantRefs(refs: RefLike[] | undefined, active: boolean) {
  const { pathname } = useLocation();
  const refsRef = useRef<RefLike[]>(refs ?? []);
  refsRef.current = refs ?? [];
  const timers = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());
  const key = JSON.stringify((refs ?? []).map((r) => [r.n, r.targetId]));

  useEffect(() => {
    if (!active) return undefined;
    const touched: HTMLElement[] = [];
    refsRef.current.forEach((ref) => {
      findAssistantRefElements(ref.targetId).forEach((el) => {
        el.setAttribute('data-assistant-ref-n', String(Math.trunc(Number(ref.n))));
        touched.push(el);
      });
    });
    return () => {
      touched.forEach((el) => {
        el.removeAttribute('data-assistant-ref-n');
        el.removeAttribute('data-assistant-ref-pulse');
      });
    };
  }, [active, key, pathname]);

  useEffect(() => {
    const pending = timers.current;
    return () => {
      pending.forEach((t) => clearTimeout(t));
      pending.clear();
    };
  }, []);

  const elementsFor = (n: number): HTMLElement[] => {
    const ref = refsRef.current.find((r) => r.n === n);
    return ref ? findAssistantRefElements(ref.targetId) : [];
  };

  const scroll = (el: HTMLElement) =>
    el.scrollIntoView({ block: 'center', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });

  const pulse = useCallback((n: number) => {
    const els = elementsFor(n);
    els.forEach((el) => {
      el.setAttribute('data-assistant-ref-pulse', 'true');
      const t = setTimeout(() => {
        el.removeAttribute('data-assistant-ref-pulse');
        timers.current.delete(t);
      }, PULSE_MS);
      timers.current.add(t);
    });
    const first = els[0];
    if (first) {
      const rect = first.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > window.innerHeight) scroll(first);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reveal = useCallback((n: number) => {
    const first = elementsFor(n)[0];
    if (first) scroll(first);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { pulse, reveal };
}
